/**
 * Every tab of the calculation detail page, in the order `Calculation` declares
 * its panels.
 *
 * `Tabs` addresses a panel by its position in the flattened panel list, so the
 * `calculation-tabs` query parameter is an index and nothing else. Linking to
 * that number by hand breaks the moment a tab is inserted, which is why callers
 * ask for a tab by name through `calculationTabPath` instead.
 *
 * A new tab is registered here at the position it is declared in
 * `calculation.tsx` — `calculationTabCyId` will not type-check until it is, and
 * the position is what the links resolve to.
 */
export const CALCULATION_TAB_ORDER = ["general", "services", "parsc", "files", "members"] as const;

export type CalculationTab = (typeof CALCULATION_TAB_ORDER)[number];

/**
 * The tabs that hold the same index for every calculation. `parsc` is rendered
 * only for PARSC calculations and a hidden panel takes no index, so every tab
 * after it sits one position lower for the other types — those cannot be linked
 * to without knowing the calculation's type, and are deliberately not linkable
 * here.
 */
export type FixedCalculationTab = Extract<CalculationTab, "general" | "services">;

/**
 * Build the path to a calculation with one of its fixed-position tabs selected.
 *
 * @param calculationId - The calculation to open.
 * @param tab - The tab to select, by name.
 * @returns A path including the `calculation-tabs` index the named tab currently has.
 */
export function calculationTabPath(calculationId: string, tab: FixedCalculationTab) {
	return `/calculations/${calculationId}?calculation-tabs=${CALCULATION_TAB_ORDER.indexOf(tab)}`;
}

/**
 * The `data-cy` id of a calculation tab, so a panel and the registry above
 * cannot disagree about which tabs exist.
 *
 * @param tab - The tab to identify, by name.
 * @returns The tab's `data-cy` id.
 */
export function calculationTabCyId(tab: CalculationTab): `${CalculationTab}-tab` {
	return `${tab}-tab`;
}
